import { faArrowsAlt, faCloudUploadAlt, faPencilAlt, faTimesCircle } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import isEqual from "react-fast-compare"
import { useTranslation } from "react-i18next"
import { Button } from "../../App.styles"
import ConfirmationModal from "../ConfirmationModal"
import Spinner from '../Spinner'
import ModalAiPhoto from "./ModalAiPhoto"
import ModalHtml from "./ModalHtml"
import { useUpdateNubsMutation, useUploadNubsMutation } from "./NubsUpload.state"
import {
    ActionWrapper,
    DescriptionHelp,
    DraggableItem,
    DroppableList,
    FileWrapper,
    ImageUploadWrapper,
    NubsActionButton,
    NubsActionButtons,
    NubsBlankPlaceholder,
    NubsBody,
    NubsContainer,
    NubsDragIcon,
    NubsFragment,
    NubsLoader,
    UploadBody,
    UploadBtn,
} from "./NubsUpload.style"
import { acceptedImages, acceptedVideos } from '../../consts/accepted-files'
import RowInfo from '../RowInfo'
import ModalNubsDescription from './ModalNubsDescription'
import UploadProgress from './UploadProgress'
import Nub from './Nub'

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
    opacity: isDragging ? '0.5' : '1',
    ...draggableStyle,
});

const getListStyle = () => {
    return {}
};

const onDragEnd = (items, callback) => (result) => {
    // dropped outside the list
    if (!result.destination) {
        return;
    }

    const _items = reorder(
        items,
        result.source.index,
        result.destination.index
    );

    callback && callback(_items);
}

const NubsUpload = ({ label, name, useQuery, taskId, useDeleteNubMutation, max = 0 }) => {
    const { t } = useTranslation();
    const [htmlModalVisible, setHtmlModalVisible] = useState(false)
    const [aiPhotoModalVisible, setAiPhotoModalVisible] = useState(false)
    const [pendingAiPhotoCount, setPendingAiPhotoCount] = useState(0)
    const [collection, setCollection] = useQuery();
    const [items, setItems] = useState([])
    const [editId, setEditId] = useState(null)
    const { id } = collection || {}
    let timeoutRef = null
    const wrapperRef = useRef()
    const inputFileRef = useRef(null)
    const containerRef = useRef(null)
    const handleFileLabel = (e) => {
        if (e.key !== "Enter" || inputFileRef.current === null) return;
        inputFileRef.current.click();
    };

    const deleteNub = useDeleteNubMutation({ useQuery, taskId })
    const { isLoading: isUploading, mutateAsync: uploadNub, progress } = useUploadNubsMutation({ taskId, useQuery })
    const { isLoading: isUpdating, mutateAsync: updateNub } = useUpdateNubsMutation({ taskId })

    const upload = async (file) => {
        if (file) {
            await uploadNub(file);
            clearTimeout(timeoutRef)
        }
    };

    const fileId = name && id ? `${name}-${id}-upload` : `new-upload-${id}`

    const handleOnDragEnd = onDragEnd(items, async sourceList => {
        const byId = item => n => parseInt(n.id) === parseInt(item.id)
        const list = sourceList.map(l => ({
            ...l,
            // position starts from 1 (non zero index)
            position: sourceList.findIndex(byId(l)) + 1
        }))
        const promises = list.filter((l, index) => {
            // Filters out unchanged positions
            const inCollection = collection.nubs.findIndex(byId(l))
            return inCollection !== index
        }).map(async (l) => {
            await updateNub({
                id: l.id,
                position: l.position,
            })
        })
        await Promise.all(promises)
        await setCollection({
            ...collection,
            nubs: [...list],
        })
    })

    const onDelete = async item => {
        try {
            await deleteNub(item)
        } catch { }
    }

    const renderItems = useMemo(() => {
        const byPosition = (a, b) => {
            return a.position - b.position
        }
        return items
            .sort(byPosition)
            .map(nub => ({
                ...nub,
                // Fix Invariant failed: Draggable requires a [string] draggableId.
                id: String(nub.id),
                content: (
                    <Nub
                        nub={nub} 
                        collection={collection} 
                        containerRef={containerRef} 
                    />
                )
            }))
    }, [items, collection, containerRef])

    const loading = useMemo(() => {
        return isUpdating || isUploading
    }, [isUpdating, isUploading])

    const placeholderLimit = max || 5
    const pendingAiPhotoSlots = Math.min(pendingAiPhotoCount, Math.max(placeholderLimit - renderItems.length, 0))

    const onAiPhotoGenerateStart = () => {
        setPendingAiPhotoCount((count) => count + 1)
    }

    const onAiPhotoGenerateComplete = () => {
        setPendingAiPhotoCount((count) => Math.max(count - 1, 0))
    }

    useEffect(() => {
        if (!isEqual((collection?.nubs || []), items) && !loading) {
            setItems(collection?.nubs)
        }
    }, [collection?.nubs, items, loading])

    useEffect(() => {
        // Auto scroll to the left when the nubs are longer than the container
        // so that the action buttons (Add image/HTML) are visible
        const resizeObserver = new ResizeObserver((entries) => {
            const updatedWidth = entries?.[0]?.target?.clientWidth
            if (
                updatedWidth &&
                nubsBody?.parentNode?.clientWidth < updatedWidth
            ) {
                nubsBody.parentNode.scrollTo({
                    top: 0,
                    right: updatedWidth,
                    behavior: 'smooth'
                })
            }
        })
        let wrapper = null
        let nubsBody = null
        if (wrapperRef?.current) {
            wrapper = wrapperRef.current
            nubsBody = wrapper.querySelector('.nubs__body')
            resizeObserver.observe(nubsBody);
        }
        return () => {
            resizeObserver.unobserve(nubsBody);
        }
    }, [])

    useEffect(() => {
        if (!!editId) {
            setHtmlModalVisible(true)
        }
    }, [editId])

    return (
        <NubsFragment ref={wrapperRef}>
            <ImageUploadWrapper className="form-outline">
                {label && <label className="form-label">{label}</label>}
                <NubsContainer ref={containerRef}>
                    <NubsBody className="nubs__body" container={containerRef?.current}>
                        <UploadBody className="p-4" container={containerRef?.current}>
                            <FontAwesomeIcon className="mb-4" icon={faCloudUploadAlt} />
                            <ActionWrapper>
                                <UploadBtn htmlFor={fileId} tabIndex={0} onKeyUp={handleFileLabel}>
                                    <FileWrapper>
                                        <input
                                            accept={[
                                                ...acceptedVideos,
                                                ...acceptedImages,
                                            ].join(',')}
                                            className="form-control"
                                            id={fileId}
                                            type="file"
                                            onChange={(evt) => {
                                                const { target } = evt
                                                upload(target.files[0])
                                            }}
                                            ref={inputFileRef}
                                        />
                                    </FileWrapper>
                                    <Button tag="span" color="primary" className="btn--flat">
                                        {t('Add Image, Video or File')}
                                    </Button>
                                </UploadBtn>
                                <Button onClick={() => setAiPhotoModalVisible(true)} className="btn--flat">
                                    <span dangerouslySetInnerHTML={{ __html: t('AI generated photo') }} />
                                </Button>
                                <DescriptionHelp>
                                    <RowInfo help={<ModalNubsDescription />} isFull>
                                        <Button onClick={() => setHtmlModalVisible(true)} className="btn--flat">
                                            <span dangerouslySetInnerHTML={{ __html: t(`Add Text Description`) }} />
                                        </Button>
                                    </RowInfo>
                                </DescriptionHelp>
                            </ActionWrapper>
                        </UploadBody>
                        {Array.from({ length: pendingAiPhotoSlots }).map((_, index) => (
                            <NubsBlankPlaceholder
                                key={`nubs-generating-placeholder-${index}`}
                                className="align-items-center justify-content-center text-center p-3 text-muted"
                                container={containerRef?.current}
                            >
                                <div>
                                    <Spinner />
                                    <small className="d-block mt-2">
                                        {t('Generating in the background.')}
                                        <br />
                                        {t('This can take up to 5 minutes.')}
                                    </small>
                                </div>
                            </NubsBlankPlaceholder>
                        ))}
                        <DragDropContext onDragEnd={handleOnDragEnd}>
                            <Droppable
                                droppableId="droppable-nubs"
                                direction="horizontal"
                            >
                                {(provided) => (
                                    <DroppableList
                                        ref={provided.innerRef}
                                        style={getListStyle()}
                                        {...provided.droppableProps}
                                        tabIndex={-1}
                                    >
                                        {
                                            renderItems.length > 0
                                                ? renderItems.map((item, index) => {
                                                    const hasId = !!item.id && item.id !== 'undefined'
                                                    const hasChunk = !!item.chunk
                                                    return (
                                                        <Draggable key={item.id} draggableId={item.id} index={index} isDragDisabled={!hasId}>
                                                            {(provided, snapshot) => (
                                                                <DraggableItem
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={getItemStyle(
                                                                        snapshot.isDragging,
                                                                        provided.draggableProps.style
                                                                    )}
                                                                    tabIndex={-1}
                                                                >
                                                                    {
                                                                        hasId || hasChunk
                                                                            ? (
                                                                                <NubsActionButtons className="nubs-action__buttons">
                                                                                    {
                                                                                        hasChunk
                                                                                            ? (
                                                                                                <NubsActionButton 
                                                                                                    title={t(`Edit Nub`)} 
                                                                                                    color="link" 
                                                                                                    tabIndex={-1} 
                                                                                                    type="button"
                                                                                                    onClick={() => {
                                                                                                        setEditId(item.id)
                                                                                                    }}
                                                                                                >
                                                                                                    <FontAwesomeIcon icon={faPencilAlt} />
                                                                                                </NubsActionButton>
                                                                                            )
                                                                                            : null
                                                                                    }
                                                                                    {
                                                                                        hasId
                                                                                            ? (
                                                                                                <ConfirmationModal
                                                                                                    title={t("Confirm Deletion")}
                                                                                                    body={t("Are you sure you wish to delete?")}
                                                                                                    confirmLabel={t("Delete")}
                                                                                                    confirmAction={() => onDelete(item)}
                                                                                                >
                                                                                                    <NubsActionButton 
                                                                                                        title={t(`Delete Nub`)} 
                                                                                                        color="link" 
                                                                                                        tabIndex={-1} 
                                                                                                        type="button"
                                                                                                    >
                                                                                                        <FontAwesomeIcon icon={faTimesCircle} />
                                                                                                    </NubsActionButton>
                                                                                                </ConfirmationModal>
                                                                                            )
                                                                                            : null
                                                                                    }
                                                                                </NubsActionButtons>
                                                                            )
                                                                            : null
                                                                    }
                                                                    {item.content}
                                                                    {
                                                                        hasId
                                                                            ? (
                                                                                <NubsDragIcon>
                                                                                    <FontAwesomeIcon icon={faArrowsAlt} />
                                                                                </NubsDragIcon>
                                                                            )
                                                                            : null
                                                                    }
                                                                </DraggableItem>
                                                            )}
                                                        </Draggable>
                                                    )
                                                })
                                                : null
                                        }
                                        {provided.placeholder}
                                    </DroppableList>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </NubsBody>
                </NubsContainer>
            </ImageUploadWrapper>
            <ModalHtml
                onDismiss={() => {
                    // Prevent closing HTML modal when user selects text 
                    // inside of the HTML editor
                    const selectedText = window.getSelection().toString();
                    if (selectedText) {
                        return false;
                    }
                    setHtmlModalVisible(false)
                    setEditId(null)
                }}
                show={htmlModalVisible}
                useQuery={useQuery}
                taskId={taskId}
                editId={editId}
            />
            <ModalAiPhoto
                onDismiss={() => setAiPhotoModalVisible(false)}
                show={aiPhotoModalVisible}
                useQuery={useQuery}
                taskId={taskId}
                onGenerateStart={onAiPhotoGenerateStart}
                onGenerateComplete={onAiPhotoGenerateComplete}
            />
            <NubsLoader isLoading={loading}>
                <Spinner />
                <UploadProgress progress={progress} />
            </NubsLoader>
        </NubsFragment>
    );
};

export default NubsUpload;
